import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { BackgroundLayout } from './components/BackgroundLayout';

export const Safety = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const { stores } = useDependencies();
  const flow = stores.reviewFlow;
  const parentId = pathId ? Number(pathId) : undefined;

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'UrbanSitter - Safety Feedback';
  }, []);

  useEffect(() => {
    if (!parentId) return;
    if (!flow.isValidForParent(parentId)) {
      navigate(`/parent/${parentId}/feedback`, { replace: true });
    }
  }, [parentId, flow, navigate]);

  const review = flow.review;
  const canSubmit = review?.safetyConcern !== undefined && !submitting;

  const submit = async (): Promise<void> => {
    if (!canSubmit || !parentId) return;
    setSubmitting(true);
    try {
      if (review?.id) {
        await flow.saveReview();
      }
      navigate(`/parent/${parentId}/feedback/message`);
    } catch {
      toast.error(t('pages.parentFeedback.safety.submitFailed'));
      setSubmitting(false);
    }
  };

  const options: Array<{ value: boolean; labelKey: string; bodyKey: string; id: string }> = [
    {
      value: true,
      id: 'safety-concern-yes',
      labelKey: 'pages.parentFeedback.safety.yesLabel',
      bodyKey: 'pages.parentFeedback.safety.yesBody',
    },
    {
      value: false,
      id: 'safety-concern-no',
      labelKey: 'pages.parentFeedback.safety.noLabel',
      bodyKey: 'pages.parentFeedback.safety.noBody',
    },
  ];

  return (
    <BackgroundLayout id='parent-review'>
      <h1 className='text-2xl font-semibold mb-2'>
        {t('pages.parentFeedback.safety.heading')}
      </h1>
      <h2 className='text-base text-neutral-600 mb-1'>
        {t('pages.parentFeedback.safety.subheading1')}
      </h2>
      <h2 className='text-base text-neutral-600 mb-6'>
        {t('pages.parentFeedback.safety.subheading2')}
      </h2>

      <div className='space-y-4 mb-6'>
        {options.map((opt) => {
          const selected = review?.safetyConcern === opt.value;
          return (
            <label
              key={opt.id}
              htmlFor={opt.id}
              className={`block rounded border px-4 py-3 cursor-pointer us-card ${
                selected ? 'border-sapphire bg-sapphire-50' : 'border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              <input
                id={opt.id}
                type='radio'
                name='safety-concern'
                checked={selected}
                onChange={(): void => flow.setReview({ safetyConcern: opt.value })}
                className='sr-only'
              />
              <span className='block font-bold'>{t(opt.labelKey)}</span>
              <p className='mb-0 font-light text-sm'>{t(opt.bodyKey)}</p>
            </label>
          );
        })}
      </div>

      <div className='text-center mt-5'>
        <Button
          buttonType={ButtonType.primary}
          onClick={submit}
          disabled={!canSubmit}
        >
          {t('pages.parentFeedback.safety.next')}
        </Button>
      </div>
    </BackgroundLayout>
  );
});
